import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "chat-page" };
const _hoisted_2 = {
    key: 0,
    class: "state-box"
};
const _hoisted_3 = {
    key: 1,
    class: "state-box error"
};
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { csCredential, csInit, csMessageList, csSessionCurrent, csSessionStart, setSessionToken, } from '@/api/cs';
import { ACCESS_ID } from '@/config';
import { SESSION_STATUS } from '@/types/cs';
import { destroyTim, fetchC2CHistory, loginTim, onTimMessage, sendImage, sendText } from '@/utils/tim';
import { appendChatMessage, mergeChatMessages, normalizeDisplayMessage, revokeBlobImageUrl, } from '@/utils/messages';
import { loadUnifiedHistory, saveUnifiedHistory } from '@/utils/unifiedHistory';
import { toMessageTimestamp } from '@/utils/messageTime';
import { getVisitorId, saveVisitorId } from '@/utils/visitor';
import { getOpenidFromQuery } from '@/utils/wechat';
import { BOT_CONNECTING, BOT_NAME, BOT_NO_MATCH, BOT_WELCOME, FAQ_ITEMS, isHumanRequest, matchFaqByText, } from '@/constants/faq';
import ChatPanel from './components/ChatPanel.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'index',
    setup(__props) {
        const route = useRoute();
        const loading = ref(true);
        const error = ref('');
        const visitorId = ref('');
        const imUid = ref('');
        const accessId = ref('');
        const displayName = ref('');
        const currentSession = ref(null);
        const queueInfo = ref(null);
        const messages = ref([]);
        const messagesLoading = ref(false);
        const sending = ref(false);
        const botTyping = ref(false);
        /** bot: 机器人问答；human: 已转人工，输入走 IM */
        const mode = ref('bot');
        let queueTimer = null;
        let localSeq = 0;
        let humanEntered = false;
        let botTypingTimer;
        const agentName = computed(() => currentSession.value?.agentDisplayName || BOT_NAME);
        const faqChips = computed(() => FAQ_ITEMS.map((item) => ({ id: item.id, question: item.question })));
        function resolveSessionStatus(status) {
            if (status == null || status === '')
                return null;
            const value = Number(status);
            return Number.isFinite(value) ? value : null;
        }
        function isSessionEnded(session) {
            if (!session)
                return true;
            if (session.closeTime)
                return true;
            const status = resolveSessionStatus(session.status);
            if (status === SESSION_STATUS.CLOSED || status === SESSION_STATUS.CANCELLED)
                return true;
            const statusText = session.statusText?.trim();
            if (statusText && /结束|关闭|取消|已完成/.test(statusText))
                return true;
            return false;
        }
        function isSessionActive(session) {
            if (!session || isSessionEnded(session))
                return false;
            return resolveSessionStatus(session.status) === SESSION_STATUS.ACTIVE;
        }
        function isSessionQueue(session) {
            if (!session || isSessionEnded(session))
                return false;
            return resolveSessionStatus(session.status) === SESSION_STATUS.QUEUE;
        }
        function normalizeCurrentSession(session) {
            if (isSessionEnded(session))
                return null;
            return session ?? null;
        }
        /** 当前会话状态以 /kf/h5/session/current 为准；/init 仅做访客与 IM 初始化 */
        async function loadCurrentSession(visitor) {
            try {
                const { currentSession } = await csSessionCurrent({ visitorId: visitor });
                return normalizeCurrentSession(currentSession);
            }
            catch {
                return null;
            }
        }
        /** 进入页面：始终先机器人；会话信息写入 currentSession 供后续转人工使用 */
        async function routeByCurrentSession(session, options) {
            currentSession.value = session;
            queueInfo.value = null;
            stopQueuePolling();
            mode.value = 'bot';
            if (options?.showBotWelcome !== false) {
                pushBotMessage({ content: BOT_WELCOME, showOptions: true });
            }
            if (session && isSessionActive(session)) {
                messagesLoading.value = true;
                try {
                    await syncHumanMessages(session);
                }
                catch {
                    // ignore sync errors
                }
                messagesLoading.value = false;
            }
        }
        function localId() {
            localSeq += 1;
            return `local-${Date.now()}-${localSeq}`;
        }
        function persist() {
            if (visitorId.value)
                saveUnifiedHistory(visitorId.value, messages.value);
        }
        function clearPreviousOptions() {
            for (const msg of messages.value) {
                if (msg.role === 'bot')
                    msg.showOptions = false;
            }
        }
        function pushBotMessage(opts) {
            if (opts.showOptions)
                clearPreviousOptions();
            messages.value.push({
                msgId: localId(),
                fromImUid: 'bot',
                toImUid: imUid.value || '',
                msgType: 'local',
                kind: 'text',
                content: opts.content,
                title: opts.title,
                role: 'bot',
                showOptions: opts.showOptions,
                msgTime: Date.now(),
                fromSelf: false,
            });
        }
        function pushUserMessage(content) {
            messages.value.push({
                msgId: localId(),
                fromImUid: imUid.value || 'user',
                toImUid: 'bot',
                msgType: 'local',
                kind: 'text',
                content,
                role: 'user',
                msgTime: Date.now(),
                fromSelf: true,
            });
        }
        function pushSystemMessage(content) {
            messages.value.push({
                msgId: localId(),
                fromImUid: 'system',
                toImUid: '',
                msgType: 'local',
                content,
                role: 'system',
                msgTime: Date.now(),
            });
        }
        function answerWithBot(item) {
            if (botTypingTimer)
                clearTimeout(botTypingTimer);
            botTyping.value = true;
            botTypingTimer = setTimeout(() => {
                botTyping.value = false;
                pushBotMessage({ title: item.question, content: item.answer, showOptions: true });
            }, 420);
        }
        function answerNoMatch() {
            if (botTypingTimer)
                clearTimeout(botTypingTimer);
            botTyping.value = true;
            botTypingTimer = setTimeout(() => {
                botTyping.value = false;
                pushBotMessage({ content: BOT_NO_MATCH, showOptions: true });
            }, 420);
        }
        function stopQueuePolling() {
            if (queueTimer) {
                clearInterval(queueTimer);
                queueTimer = null;
            }
        }
        function startQueuePolling() {
            stopQueuePolling();
            queueTimer = setInterval(async () => {
                if (!visitorId.value)
                    return;
                try {
                    const data = await csSessionCurrent({ visitorId: visitorId.value });
                    const session = normalizeCurrentSession(data.currentSession);
                    currentSession.value = session;
                    if (!session) {
                        stopQueuePolling();
                        if (mode.value === 'human') {
                            mode.value = 'bot';
                            pushBotMessage({ content: BOT_WELCOME, showOptions: true });
                        }
                        return;
                    }
                    if (isSessionActive(session) && session.agentImUid) {
                        stopQueuePolling();
                        queueInfo.value = null;
                        await enterHuman(session);
                    }
                }
                catch {
                    // ignore polling errors
                }
            }, 4000);
        }
        /** 合并人工历史消息（来自后端接口），按 msgId 去重并按时间排序 */
        function mergeHumanRows(rows) {
            const existing = new Set(messages.value.map((item) => item.msgId).filter((id) => !!id));
            for (const row of rows) {
                if (!row)
                    continue;
                const normalized = normalizeDisplayMessage({
                    ...row,
                    content: row.content ?? '',
                    fromSelf: row.fromImUid === imUid.value,
                });
                if (normalized.msgId && existing.has(normalized.msgId))
                    continue;
                messages.value.push(normalized);
                if (normalized.msgId)
                    existing.add(normalized.msgId);
            }
            messages.value.sort((a, b) => toMessageTimestamp(a.msgTime) - toMessageTimestamp(b.msgTime));
        }
        /** 从后端 + TIM 云端合并拉取人工消息（含用户离线期间客服发送的消息） */
        async function syncHumanMessages(session) {
            if (!session.sessionId || !session.agentImUid)
                return;
            const [apiResult, timRows] = await Promise.all([
                csMessageList({
                    sessionId: session.sessionId,
                    visitorId: visitorId.value,
                    currentPage: 1,
                    pageSize: 100,
                }).catch(() => null),
                fetchC2CHistory(session.agentImUid, 100).catch(() => []),
            ]);
            const apiRows = Array.isArray(apiResult?.rows) ? apiResult.rows : [];
            const merged = mergeChatMessages(apiRows, timRows, imUid.value);
            if (merged.length)
                mergeHumanRows(merged);
        }
        /** 进入人工客服：插入系统提示并加载人工历史 */
        async function enterHuman(session) {
            mode.value = 'human';
            if (!humanEntered) {
                humanEntered = true;
                pushSystemMessage(`【人工客服】${session.agentDisplayName || '客服'}为您服务`);
            }
            if (!session.sessionId || !session.agentImUid)
                return;
            messagesLoading.value = true;
            try {
                await syncHumanMessages(session);
            }
            catch {
                // ignore history errors
            }
            messagesLoading.value = false;
            persist();
        }
        async function refreshHumanMessagesIfNeeded() {
            const session = currentSession.value;
            if (mode.value !== 'human' || !isSessionActive(session) || !session?.agentImUid) {
                return;
            }
            try {
                await syncHumanMessages(session);
                persist();
            }
            catch {
                // ignore refresh errors
            }
        }
        async function onPageVisible() {
            if (document.visibilityState !== 'visible' || !visitorId.value)
                return;
            const session = await loadCurrentSession(visitorId.value);
            if (mode.value === 'human' && !session) {
                await routeByCurrentSession(null);
                persist();
                return;
            }
            currentSession.value = session;
            await refreshHumanMessagesIfNeeded();
        }
        /** 用户请求转人工：先查 /session/current，无进行中会话再 /session/start */
        async function connectHuman() {
            if (mode.value === 'human')
                return;
            const session = await loadCurrentSession(visitorId.value);
            if (isSessionActive(session) && session?.agentImUid) {
                currentSession.value = session;
                await enterHuman(session);
                return;
            }
            if (isSessionQueue(session)) {
                currentSession.value = session;
                mode.value = 'human';
                pushSystemMessage(BOT_CONNECTING);
                startQueuePolling();
                return;
            }
            mode.value = 'human';
            pushSystemMessage(BOT_CONNECTING);
            try {
                const data = await csSessionStart({ accessId: accessId.value, visitorId: visitorId.value });
                setSessionToken(data.sessionToken);
                currentSession.value = normalizeCurrentSession(data.currentSession);
                queueInfo.value = data.queueInfo;
                if (isSessionActive(currentSession.value) && currentSession.value?.agentImUid) {
                    stopQueuePolling();
                    await enterHuman(currentSession.value);
                }
                else if (isSessionQueue(currentSession.value)) {
                    startQueuePolling();
                }
            }
            catch (err) {
                pushSystemMessage(err instanceof Error ? err.message : '接入人工客服失败，请稍后重试');
            }
        }
        async function applyInitData(data) {
            if (!data?.visitorId || !data?.imUid || !data?.userSig || data.sdkAppId == null) {
                throw new Error('初始化数据不完整');
            }
            visitorId.value = data.visitorId;
            imUid.value = data.imUid;
            displayName.value = data.displayName;
            setSessionToken(data.sessionToken);
            saveVisitorId(data.visitorId);
            await loginTim(data.sdkAppId, data.imUid, data.userSig, async () => {
                const cred = await csCredential({ visitorId: data.visitorId });
                return { userSig: cred.userSig };
            });
            onTimMessage((message) => {
                const session = currentSession.value;
                if (!session || !isSessionActive(session))
                    return;
                const agentUid = session.agentImUid;
                if (!agentUid)
                    return;
                if (message.fromImUid !== agentUid && message.toImUid !== agentUid)
                    return;
                appendChatMessage(messages.value, { ...message, sessionId: session.sessionId }, imUid.value);
                persist();
            });
            // 统一会话历史（机器人 + 人工）
            const history = loadUnifiedHistory(visitorId.value);
            if (history.length) {
                messages.value = history
                    .filter((item) => !(item.role === 'bot' && item.content === BOT_WELCOME))
                    .map((item) => normalizeDisplayMessage({
                    ...item,
                    fromSelf: item.fromImUid === imUid.value,
                }));
            }
            const session = await loadCurrentSession(data.visitorId);
            await routeByCurrentSession(session);
            persist();
        }
        async function bootstrap() {
            const queryAccessId = route.query.accessId || ACCESS_ID.WECHAT;
            const queryUserId = route.query.userId;
            accessId.value = queryAccessId;
            if (queryAccessId === ACCESS_ID.APP && !queryUserId) {
                error.value = '缺少 userId 参数，请从 App 正确入口进入';
                loading.value = false;
                return;
            }
            // 不走微信网页授权，公众号与普通浏览器一样按匿名访客进入
            const openid = getOpenidFromQuery();
            const storedVisitorId = queryAccessId === ACCESS_ID.WECHAT ? getVisitorId() : null;
            const INIT_TIMEOUT_MS = 30000;
            try {
                await Promise.race([
                    (async () => {
                        const data = await csInit({
                            accessId: queryAccessId,
                            userId: queryUserId,
                            visitorId: storedVisitorId,
                            openid,
                        });
                        await applyInitData(data);
                    })(),
                    new Promise((_, reject) => {
                        window.setTimeout(() => reject(new Error('页面初始化超时，请检查网络或刷新重试')), INIT_TIMEOUT_MS);
                    }),
                ]);
            }
            catch (err) {
                error.value = err instanceof Error ? err.message : '初始化失败';
            }
            finally {
                loading.value = false;
            }
        }
        function requestHuman() {
            pushUserMessage('人工客服');
            connectHuman();
        }
        function pickFaq(id) {
            const item = FAQ_ITEMS.find((faq) => faq.id === id);
            if (!item)
                return;
            if (mode.value === 'human') {
                void sendHuman(item.question);
                return;
            }
            pushUserMessage(item.question);
            answerWithBot(item);
        }
        async function sendHuman(content) {
            const session = currentSession.value;
            if (!isSessionActive(session) || !session?.agentImUid) {
                pushSystemMessage('当前会话已结束，请重新联系人工客服');
                return;
            }
            if (sending.value)
                return;
            sending.value = true;
            const optimistic = {
                sessionId: session.sessionId,
                fromImUid: imUid.value,
                toImUid: session.agentImUid,
                msgType: 'TIMTextElem',
                kind: 'text',
                content,
                msgTime: Date.now(),
                fromSelf: true,
            };
            const optimisticIndex = messages.value.length;
            messages.value.push(optimistic);
            try {
                const msgId = await sendText(session.agentImUid, content);
                if (msgId && messages.value[optimisticIndex]) {
                    messages.value[optimisticIndex].msgId = msgId;
                }
                persist();
            }
            catch (err) {
                messages.value.splice(optimisticIndex, 1);
                error.value = err instanceof Error ? err.message : '发送失败';
            }
            finally {
                sending.value = false;
            }
        }
        async function handleSend(text) {
            const content = text.trim();
            if (!content)
                return;
            if (mode.value === 'human') {
                await sendHuman(content);
                return;
            }
            // 机器人模式
            pushUserMessage(content);
            if (isHumanRequest(content)) {
                await connectHuman();
                return;
            }
            const item = matchFaqByText(content);
            if (item)
                answerWithBot(item);
            else
                answerNoMatch();
        }
        async function handleSendImage(file) {
            if (mode.value !== 'human') {
                pushSystemMessage('请先联系人工客服后再发送图片');
                return;
            }
            const session = currentSession.value;
            if (!session?.agentImUid || sending.value)
                return;
            sending.value = true;
            const previewUrl = URL.createObjectURL(file);
            const optimistic = {
                sessionId: session.sessionId,
                fromImUid: imUid.value,
                toImUid: session.agentImUid,
                msgType: 'TIMImageElem',
                kind: 'image',
                imageUrl: previewUrl,
                content: '[图片]',
                msgTime: Date.now(),
                fromSelf: true,
            };
            const optimisticIndex = messages.value.length;
            messages.value.push(optimistic);
            try {
                const sent = await sendImage(session.agentImUid, file);
                const row = messages.value[optimisticIndex];
                if (row) {
                    if (sent.msgId)
                        row.msgId = sent.msgId;
                    if (sent.imageUrl) {
                        revokeBlobImageUrl(row.imageUrl);
                        row.imageUrl = sent.imageUrl;
                    }
                }
                persist();
            }
            catch (err) {
                revokeBlobImageUrl(previewUrl);
                messages.value.splice(optimisticIndex, 1);
                error.value = err instanceof Error ? err.message : '图片发送失败';
            }
            finally {
                sending.value = false;
            }
        }
        watch(messages, () => persist(), { deep: true });
        onMounted(() => {
            bootstrap();
            document.addEventListener('visibilitychange', onPageVisible);
        });
        onBeforeUnmount(() => {
            document.removeEventListener('visibilitychange', onPageVisible);
            stopQueuePolling();
            if (botTypingTimer)
                clearTimeout(botTypingTimer);
            destroyTim();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, "连接客服中…"))
                    : (error.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(error.value), 1 /* TEXT */))
                        : (_openBlock(), _createBlock(ChatPanel, {
                            key: 2,
                            messages: messages.value,
                            "agent-name": agentName.value,
                            loading: messagesLoading.value,
                            disabled: sending.value,
                            typing: botTyping.value,
                            "faq-items": faqChips.value,
                            onSend: handleSend,
                            onSendImage: handleSendImage,
                            onPickFaq: pickFaq,
                            onRequestHuman: requestHuman
                        }, null, 8 /* PROPS */, ["messages", "agent-name", "loading", "disabled", "typing", "faq-items"]))
            ]));
        };
    }
});
